'use client'

import Image from 'next/image'
import { getReadableImageUrl } from '@/lib/directus'
import { sanitizeHTML } from '@/lib/utils'

interface SecProceduresProps {
	heading?: {
		title?: string
	}
	text?: {
		text?: string
	}
	image?: {
		alt: string
		image_file: {
			image: {
				id: string
				filename_disk: string
			}
		}
	}
	content_collection?: {
		id: string
		site_content_collections_id: {
			collection: {
				item: {
					title: string | null
					text: string | null
				}
			}[]
		}
	}[]
}

export const fields = [
	// headings
	'heading.title',

	// text
	'text.text',

	// image
	'image.alt',
	'image.image_file.image.id',
	'image.image_file.image.filename_download',

	// content collection
	'content_collection.id',
	'content_collection.site_content_collections_id.collection.item:site_content.title',
	'content_collection.site_content_collections_id.collection.item:site_content.text',
] as const

export default function SecProcedures({ data }: { data: SecProceduresProps }) {
	return (
		<section id='procedures' className='SecProcedures relative bg-sand-light py-16 overflow-hidden'>
			<div className='container relative'>
				<div className='grid lg:grid-cols-12 gap-8'>
					{/* Left Column - Image */}
					<div className='md:col-span-12 lg:col-span-4 relative lg:-ml-32 lg:-mt-32'>
						<div className='relative w-full lg:w-[calc(100%+8rem)] h-[30rem] lg:h-full lg:-ml-32 overflow-hidden rounded-br-[4.125rem]'>
							<Image
								src={getReadableImageUrl(
									data?.image?.image_file?.image?.id || '',
									data?.image?.image_file?.image?.filename_disk || ''
								)}
								alt={data?.image?.alt || ''}
								fill
								className='object-cover object-center'
								priority
							/>
						</div>
					</div>

					{/* Right Column - Content */}
					<div className='md:col-span-12 lg:col-span-8'>
						{/* Header */}
						<h2 className='mb-8'>{data?.heading?.title || ''}</h2>
						<div
							className='mb-8 md:mb-16'
							dangerouslySetInnerHTML={sanitizeHTML(data?.text?.text || '')}
						/>

						{/* Top Two Cards */}
						<div className='grid grid-cols-1 xl:grid-cols-2 gap-8'>
							{data?.content_collection?.slice(0, 2).map((card, index) => {
								const titleItem = card.site_content_collections_id.collection.find(c => c.item.title);
								const textItem = card.site_content_collections_id.collection.find(c => c.item.text);
								
								return (
									<div key={card.id} className='bg-[#E3D5CA] rounded-lg p-8'>
										<div className='flex gap-6 max-md:flex-wrap max-md:justify-center'>
											<span className='text-3xl 3xl:text-4xl font-brandon'>
												{index + 1}
											</span>
											<div>
												<h3 className='max-md:font-medium max-md:text-xl max-md:text-center mb-6 max-md:uppercase'>
													{titleItem?.item.title || ''}
												</h3>
												<div
													dangerouslySetInnerHTML={sanitizeHTML(textItem?.item.text || '')}
												/>
											</div>
										</div>
									</div>
								)
							})}
						</div>
					</div>

					{/* Bottom Three Cards - Aligned with Image */}
					{data?.content_collection?.slice(2).map((card, index) => {
						const titleItem = card.site_content_collections_id.collection.find(c => c.item.title);
						const textItem = card.site_content_collections_id.collection.find(c => c.item.text);
						
						return (
							<div key={card.id} className='md:col-span-12 xl:col-span-4'>
								<div className='bg-[#E3D5CA] rounded-lg p-8 h-full'>
									<div className='flex gap-6 max-md:flex-wrap max-md:justify-center'>
										<span className='text-3xl 3xl:text-4xl font-brandon'>
											{index + 3}
										</span>
										<div>
											<h3 className='max-md:font-medium max-md:text-xl max-md:text-center mb-6 max-md:uppercase'>
												{titleItem?.item.title || ''}
											</h3>
											<div
												dangerouslySetInnerHTML={sanitizeHTML(textItem?.item.text || '')}
											/>
										</div>
									</div>
								</div>
							</div>
						)
					})}
				</div>
			</div>
		</section>
	)
}